import { encodeKey } from "./types";

const ua = () => navigator.userAgent;
export const isWeChat = () => /MicroMessenger/i.test(ua());
export const isIOS = () => /iPhone|iPad|iPod/i.test(ua()) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isStandalone = () =>
  (navigator as any).standalone === true || window.matchMedia?.("(display-mode: standalone)").matches;
export const isIOSSafari = () => isIOS() && !isWeChat() && !/CriOS|FxiOS|EdgiOS|OPiOS|QQ\/|MQQBrowser|UCBrowser|baiduboxapp/i.test(ua());
export const isMobile = () => /Android|iPhone|iPad|iPod/i.test(ua()) || isIOS();

const url = (key: string) => `/webdav/${encodeKey(key)}`;
const blobs = new Map<string, Promise<Blob>>();
/** 预先下载图片内容，这样点击“保存”时可以立即调起系统分享（iOS 要求在点击后立刻调用） */
export function prefetch(key: string) {
  if (!blobs.has(key)) {
    const p = fetch(url(key), { credentials: "same-origin" }).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.blob();
    });
    p.catch(() => blobs.delete(key));
    blobs.set(key, p);
    if (blobs.size > 30) blobs.delete(blobs.keys().next().value as string);
  }
  return blobs.get(key)!;
}

export type SaveResult = "shared" | "downloaded" | "cancelled" | "wechat" | "retry" | "error";
export type SaveEvent = { name: string; url: string; isImage: boolean };

function emit(type: string, detail: any) { window.dispatchEvent(new CustomEvent(type, { detail })); }

function plainDownload(href: string, name: string) {
  const a = document.createElement("a");
  a.href = href; a.download = name; a.rel = "noopener";
  document.body.appendChild(a); a.click(); a.remove();
}

/** 保存文件：手机上优先调起系统分享面板（可选“存储图像”），电脑上直接下载，微信里给出提示 */
export async function saveFile(key: string, name: string, contentType?: string): Promise<SaveResult> {
  const isImage = (contentType || "").startsWith("image/") || /\.(png|jpe?g|gif|webp|heic|bmp)$/i.test(name);
  if (isWeChat()) { emit("fd-save-hint", { name, url: url(key), isImage } as SaveEvent); return "wechat"; }
  const nav: any = navigator;
  if (isMobile() && nav.share && nav.canShare) {
    try {
      const blob = await prefetch(key);
      const file = new File([blob], name, { type: blob.type || contentType || "application/octet-stream" });
      if (nav.canShare({ files: [file] })) {
        try {
          await nav.share({ files: [file] });
          return "shared";
        } catch (e: any) {
          if (e?.name === "AbortError") return "cancelled";
          if (e?.name === "NotAllowedError") { emit("fd-toast", { msg: "图片已准备好，请再点一次「保存到相册」", sev: "success" }); return "retry"; }
          throw e;
        }
      }
    } catch (e) {
      console.log("share failed, fallback to download", e);
    }
  }
  plainDownload(url(key), name);
  return "downloaded";
}
